import {createSlice} from "@reduxjs/toolkit"
import {newSearch, workspaceSearchSlice} from "./workspaceSearchSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"
import {loadWorkspace} from "./workspaceSlice"

const initialState = {
	position: 0,
	currentView: "noteTree",
	viewScrollPositionMap: {
		"noteTree": 0
	},
	viewLastClickedIdMap: {
		"noteTree": null
	}
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)
export const workspacePanelScrollSlice = createSlice({
	name: "workspacePanelScroll",
	initialState: {
		position: 0,
		currentView: "noteTree",
		viewScrollPositionMap: {
			"noteTree": 0
		},
		viewLastClickedIdMap: {
			"noteTree": null
		},
		byWorkspace: {}
	},
	reducers: {
		setWorkspaceScrollPosition: (state, action) => {
			const {newPosition, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.position = newPosition
			workspace.viewScrollPositionMap[workspace.currentView] = newPosition
		},
		setViewLastClickedId: (state, action) => {
			const {noteId, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const viewName = workspace.currentView
			workspace.viewLastClickedIdMap[viewName] = noteId
		},
		removeView: (state, action) => {
			const {viewToRemove, viewToGoTo, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const nextViewsState = {...workspace.viewScrollPositionMap}
			delete nextViewsState[viewToRemove]
			const nextLastClickedState = {...workspace.viewLastClickedIdMap}
			delete nextLastClickedState[viewToRemove]
			workspace.viewScrollPositionMap = nextViewsState
			workspace.viewLastClickedIdMap = nextLastClickedState
			workspace.currentView = viewToGoTo
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				workspaceSearchSlice.actions.openExistingSearch,
				(state, action) => {
					const {index, workspaceId} = action.payload
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.currentView = "ws" + index
				}
			)
			.addCase(
				workspaceSearchSlice.actions.removeWorkspaceSearch,
				(state, action) => {
					const {index, workspaceId} = action.payload
					const nextAction = {...action}
					const nextIndex = index === 0 ? "noteTree" : "ws" + index - 1 // if tab 0 is closed, got to noteTree, otherwise decrement
					nextAction.payload = {
						viewToRemove: "ws" + action.payload,
						viewToGoTo: nextIndex,
						workspaceId
					}
					workspacePanelScrollSlice.caseReducers.removeView(state, nextAction)
				}
			)
			.addCase(
				newSearch.fulfilled,
				(state, action) => {
					const {workspaceId, nextIndex} = action.payload
					const workspace = ensureWorkspace(state, workspaceId)
					const newTabKey = "ws" + nextIndex
					workspace.viewScrollPositionMap[newTabKey] = 0
					workspace.currentView = newTabKey
				}
			)
			.addCase(
				workspaceSearchSlice.actions.hideSearches,
				(state, action) => {
					const workspace = ensureWorkspace(state, action.payload)
					workspace.currentView = "noteTree"
				}
			)
			.addCase(
				loadWorkspace.fulfilled,
				(state, action) => {
					const workspaceId = action.meta.arg.id
					ensureWorkspace(state, workspaceId)
				}
			)
	}
})

export const {
	setWorkspaceScrollPosition,
	removeView,
	setViewLastClickedId,
} = workspacePanelScrollSlice.actions

export const selectCurrentViewKey = (state, workspaceId) => state.workspacePanelScroll.byWorkspace[workspaceId]?.currentView ?? initialState.currentView
export const getCurrentScrollPosition = (state, workspaceId) => state.workspacePanelScroll.byWorkspace[workspaceId]?.viewScrollPositionMap[state.workspacePanelScroll.byWorkspace[workspaceId]?.currentView] ?? initialState.viewScrollPositionMap.noteTree
export const getCurrentSearchLastClickedNote = (state, workspaceId) => state.workspacePanelScroll.byWorkspace[workspaceId]?.viewLastClickedIdMap[state.workspacePanelScroll.byWorkspace[workspaceId]?.currentView] ?? initialState.viewLastClickedIdMap.noteTree
export const getLastRecordedNoteTreeScrollPosition = (state, workspaceId) => state.workspacePanelScroll.byWorkspace[workspaceId]?.viewScrollPositionMap["noteTree"] ?? initialState.viewScrollPositionMap.noteTree

export default workspacePanelScrollSlice.reducer